import AddCircleIcon from "@mui/icons-material/AddCircle"
import { Box, IconButton, Stack, styled, Typography } from "@mui/material"

import { useMemo } from "react"
import { dayjsUTC } from "../../../utils/dates"
import { extraColors } from "../../../utils/theme"
import { useAlertCuration, type BiothreatSignal } from "../AlertCurationContext"

const ClusterTooltipContainer = styled(Box)(() => ({
  p: 1,
  maxWidth: 450,
  maxHeight: 400,
  overflowY: "auto",
}))

const ClusterTooltipHeader = styled(Typography)(() => ({
  fontWeight: 800,
  color: extraColors.translucent50,
  mb: 1.5,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}))

const ClusterMemberItem = styled(Box)(() => ({
  p: 1.5,
  bgcolor: "rgba(255,255,255,0.03)",
  borderRadius: "6px",
  border: `1px solid ${extraColors.translucent5}`,
  display: "flex",
  alignItems: "center",
  gap: 2,
  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
}))

const PromoteButtonContainer = styled(Box)(() => ({
  width: 28,
  display: "flex",
  justifyContent: "center",
}))

const ClusterMemberPromoteButton: React.FC<{
  isPinned: boolean
  onPromote: () => void
}> = ({ isPinned, onPromote }) => {
  if (isPinned) return null

  return (
    <PromoteButtonContainer>
      <IconButton
        size="small"
        onClick={e => {
          e.stopPropagation()
          onPromote()
        }}
        sx={{ color: extraColors.status.navyLight, p: 0.5 }}
      >
        <AddCircleIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </PromoteButtonContainer>
  )
}

const ClusterMemberMetadata: React.FC<{
  sourceName?: string
  publishTime: string
  modernScore?: number | null
}> = ({ sourceName, publishTime, modernScore }) => {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: "center",
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.4, fontSize: "0.6rem" }}>
        {sourceName || "Unknown"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: extraColors.status.navyLight,
          fontWeight: 800,
          fontSize: "0.6rem",
        }}
      >
        {dayjsUTC(publishTime).format("MM/DD/YY h:mm A")}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 900,
          color: extraColors.status.greenLight,
          fontSize: "0.6rem",
        }}
      >
        {modernScore ? `${(modernScore * 100).toFixed(0)}%` : ""}
      </Typography>
    </Stack>
  )
}

const ClusterMemberItemContent: React.FC<{
  member: BiothreatSignal
}> = ({ member }) => {
  const { addToPinnedSignals, pinnedSignals } = useAlertCuration()
  const anchorProps = member.url
    ? {
        component: "a" as const,
        href: member.url,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }
    : {}

  return (
    <ClusterMemberItem>
      <ClusterMemberPromoteButton
        isPinned={pinnedSignals.has(member.id)}
        onPromote={() => addToPinnedSignals(member)}
      />
      <Box
        {...anchorProps}
        sx={{
          flexGrow: 1,
          cursor: member.url ? "pointer" : "default",
          textDecoration: "none",
          "&:hover": member.url
            ? {
                textDecoration: "underline",
              }
            : {},
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, mb: 0.5, display: "block", lineHeight: 1.2 }}
        >
          {member.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.6, fontSize: "0.65rem" }}
        >
          {member.sub_locations}
        </Typography>
        <ClusterMemberMetadata
          sourceName={member.source_name}
          publishTime={member.publish_time}
          modernScore={member.modern_score}
        />
      </Box>
    </ClusterMemberItem>
  )
}

export const ClusterTooltipContent = ({ clusterId }: { clusterId: string }) => {
  const { signals } = useAlertCuration()
  const members = useMemo(
    () => signals.filter(s => s.cluster_id === clusterId),
    [clusterId, signals]
  )

  return (
    <ClusterTooltipContainer>
      <ClusterTooltipHeader variant="caption">
        Cluster Members ({members.length})
      </ClusterTooltipHeader>
      <Stack spacing={1}>
        {members.map(m => {
          return <ClusterMemberItemContent key={m.id} member={m} />
        })}
      </Stack>
    </ClusterTooltipContainer>
  )
}
